import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

class ProductInventory extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#renderCurrentStock();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #getInventoryData() {
    const script = this.querySelector('script[data-inventory-variants]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[product-inventory] Could not parse inventory data:', error);
      return null;
    }
  }

  #getSelectedVariant(data) {
    if (!data?.variants?.length) return null;
    const root = this.closest('.shopify-section, dialog') ?? document;
    const picker = Array.from(root.querySelectorAll('variant-picker'))
      .find((element) => element.dataset.productId === this.dataset.productId);
    const selectedByName = new Map();

    if (picker) {
      picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        if (input.dataset.optionName) selectedByName.set(input.dataset.optionName, input.value);
      });

      picker.querySelectorAll('select[name^="options["]').forEach((select) => {
        const match = select.name.match(/^options\[(.*)\]$/);
        if (match) selectedByName.set(match[1], select.value);
      });

      const optionNames = Array.isArray(data.optionNames) ? data.optionNames : [];
      if (optionNames.length && optionNames.every((name) => selectedByName.has(name))) {
        const selectedOptions = optionNames.map((name) => selectedByName.get(name));
        const exactMatch = data.variants.find((variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === selectedOptions.length &&
          variant.options.every((value, index) => value === selectedOptions[index])
        );
        if (exactMatch) return exactMatch;
      }
    }

    const form = Array.from(root.querySelectorAll('product-form-component'))
      .find((element) => element.dataset.productId === this.dataset.productId);
    const variantId = form?.querySelector('input[name="id"]')?.value
      ?? new URL(window.location.href).searchParams.get('variant');
    if (variantId) {
      const byId = data.variants.find((variant) => String(variant.id) === String(variantId));
      if (byId) return byId;
    }

    return data.variants[0] ?? null;
  }

  #renderCurrentStock() {
    const text = this.querySelector('[data-stock-count]');
    const icon = this.querySelector('[data-stock-icon]');
    const data = this.#getInventoryData();
    if (!text || !data) return;

    const variant = this.#getSelectedVariant(data);
    if (!variant) {
      text.textContent = 'Stock count unavailable';
      return;
    }

    const quantity = Number.isFinite(Number(variant.quantity)) ? Math.max(0, Number(variant.quantity)) : 0;
    const canOrder = Boolean(variant.available) || quantity > 0;

    // Supplier feeds sometimes use very large placeholder counts (for example 8888).
    // Show exact counts only for plausible positive quantities; larger supplier counts
    // are labelled as orderable without displaying an untrustworthy exact number.
    if (quantity > 0 && quantity <= 500 && canOrder) {
      text.textContent = `In stock: ${quantity}`;
    } else if (quantity > 500 && canOrder) {
      text.textContent = 'Available to order — supplier stock can change';
    } else if (canOrder) {
      text.textContent = 'Available for order';
    } else {
      text.textContent = 'Out of stock';
    }

    if (icon) {
      icon.classList.remove('product-inventory__icon-low', 'product-inventory__icon-in_stock', 'product-inventory__icon-out_of_stock');
      icon.classList.add(`product-inventory__icon-${canOrder ? 'in_stock' : 'out_of_stock'}`);
    }
  }

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    this.#renderCurrentStock();

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        morph(this, newInventory, { childrenOnly: true });
        this.#renderCurrentStock();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
